import React from 'react';
import { motion } from 'framer-motion';
import { 
  Coins, 
  Layers, 
  Users, 
  Clock, 
  Server, 
  Settings, 
  Link2, 
  ArrowRight,
  Wallet,
  Percent,
  Hash,
  Globe,
  Zap
} from 'lucide-react';

const CurrencyStructureDiagram = ({ nodes, currencyType }) => {
  // Extract data from nodes
  const mainNode = nodes.find(n => n.id === 'currency-main');
  const reserveNode = nodes.find(n => n.type === 'reserveCurrency');
  const preallocationNodes = nodes.filter(n => n.type === 'preallocation');
  const eraNode = nodes.find(n => n.type === 'era');
  const nodeSettingsNode = nodes.find(n => n.type === 'nodeSettings');
  const advancedNode = nodes.find(n => n.type === 'advancedOptions');
  const erc20Node = nodes.find(n => n.type === 'erc20');
  const bridgeNode = nodes.find(n => n.type === 'bridgeConverter');
  const gatewayNode = nodes.find(n => n.type === 'gatewayConverter');

  const mainData = mainNode?.data || {};
  const reserves = reserveNode?.data?.reserves || [];
  const eras = eraNode?.data?.eras || (eraNode?.data ? [eraNode.data] : []);
  const preallocations = preallocationNodes.map(n => n.data).filter(d => d.identity && d.amount > 0);

  // Calculate total weight for percentages
  const totalWeight = reserves.reduce((sum, r) => sum + (r.weight || 0), 0);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { type: 'spring', stiffness: 300, damping: 25 }
    }
  };



  // Block component for consistent styling
  const Block = ({ icon: Icon, title, color, children, className = '', glowColor }) => (
    <motion.div
      variants={itemVariants}
      className={`relative bg-slate-800 rounded-xl border border-slate-600 overflow-hidden shadow-lg ${className}`}
      style={{ boxShadow: glowColor ? `0 0 20px ${glowColor}30` : undefined }}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-10`} />
      <div className="relative p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center shadow-lg`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <span className="text-sm font-semibold text-white">{title}</span>
        </div>
        {children}
      </div>
    </motion.div>
  );

  // Value display component
  const ValueRow = ({ label, value, highlight = false }) => (
    <div className="flex justify-between items-center py-1.5 border-b border-slate-600/50 last:border-0">
      <span className="text-xs text-slate-300">{label}</span>
      <span className={`text-xs font-mono ${highlight ? 'text-amber-400' : 'text-white'}`}>
        {value}
      </span>
    </div>
  );

  // Reserve currency pill
  const ReservePill = ({ currency, weight, percentage }) => (
    <div className="flex items-center gap-2 bg-slate-700 rounded-lg p-2 border border-slate-500">
      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
        <Coins className="w-3 h-3 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-white truncate">{currency}</div>
        <div className="text-[10px] text-slate-300">{(weight * 100).toFixed(1)}% weight</div>
      </div>
      <div className="text-right">
        <div className="text-xs font-bold text-amber-400">{percentage}%</div>
        <div className="text-[10px] text-slate-400">of basket</div>
      </div>
    </div>
  );

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="w-full h-full flex flex-col"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white mb-1">
          {mainData.name || 'Currency'} Structure
        </h2>
        <p className="text-sm text-slate-400">
          {currencyType?.title || 'Currency'} Configuration Overview
        </p>
      </motion.div>

      {/* Main Diagram Container */}
      <div className="flex-1 grid grid-cols-12 gap-4 auto-rows-min">
        
        {/* Left Column - Inputs */}
        <div className="col-span-4 space-y-4">
          {/* Reserve Currencies */}
          {reserves.length > 0 && (
            <Block 
              icon={Layers} 
              title="Reserve Currencies" 
              color="from-amber-500 to-orange-600"
              glowColor="#f59e0b"
            >
              <div className="space-y-2">
                {reserves.map((reserve, idx) => (
                  <ReservePill
                    key={idx}
                    currency={reserve.currency}
                    weight={reserve.weight}
                    percentage={totalWeight > 0 ? ((reserve.weight / totalWeight) * 100).toFixed(1) : 0}
                  />
                ))}
              </div>
              <div className="mt-3 pt-2 border-t border-slate-600">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-300">Total Weight</span>
                  <span className="font-mono text-amber-400">{(totalWeight * 100).toFixed(1)}%</span>
                </div>
              </div>
            </Block>
          )}

          {/* Preallocations */}
          {preallocations.length > 0 && (
            <Block 
              icon={Users} 
              title="Preallocations" 
              color="from-emerald-500 to-green-600"
              glowColor="#10b981"
            >
              <div className="space-y-2">
                {preallocations.map((prealloc, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-slate-700 rounded-lg p-2 border border-slate-500">
                    <div className="flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-emerald-400" />
                      <span className="text-xs text-white font-mono truncate max-w-[120px]">
                        {prealloc.identity}
                      </span>
                    </div>
                    <span className="text-xs font-bold text-emerald-400">
                      {Number(prealloc.amount).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </Block>
          )}

          {/* ERC-20 Mapping */}
          {erc20Node?.data?.address && (
            <Block 
              icon={Link2} 
              title="ERC-20 Mapping" 
              color="from-purple-500 to-violet-600"
              glowColor="#8b5cf6"
            >
              <div className="space-y-2">
                <ValueRow label="Contract Address" value={
                  <span className="truncate max-w-[150px] block" title={erc20Node.data.address}>
                    {erc20Node.data.address.slice(0, 10)}...{erc20Node.data.address.slice(-8)}
                  </span>
                } />
                <ValueRow label="Parent Chain" value={erc20Node.data.parent || 'vETH'} highlight />
              </div>
            </Block>
          )}

          {/* Mining Eras */}
          {eras.length > 0 && eras[0]?.reward && (
            <Block 
              icon={Clock} 
              title="Mining Eras" 
              color="from-violet-500 to-purple-600"
              glowColor="#8b5cf6"
            >
              <div className="space-y-3">
                {eras.map((era, idx) => (
                  <div key={idx} className="bg-slate-700 rounded-lg p-2 border border-slate-500">
                    <div className="text-[10px] text-violet-400 font-medium mb-1">Era {idx + 1}</div>
                    <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                      <ValueRow label="Reward" value={`${(era.reward / 100000000).toFixed(2)} coins`} />
                      <ValueRow label="Halving" value={`${era.halving?.toLocaleString()} blocks`} />
                      <ValueRow label="Decay" value={era.decay || 0} />
                      <ValueRow label="Era End" value={era.eraend || '∞'} />
                    </div>
                  </div>
                ))}
              </div>
            </Block>
          )}
        </div>

        {/* Center Column - Main Currency */}
        <div className="col-span-4 flex flex-col items-center justify-start">
          {/* Connection Lines SVG */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible" style={{ zIndex: 0 }}>
            <defs>
              <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#6366f1" stopOpacity="0.3" />
                <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#6366f1" stopOpacity="0.3" />
              </linearGradient>
            </defs>
          </svg>

          {/* Main Currency Block */}
          <motion.div
            variants={itemVariants}
            className="relative w-full max-w-sm"
          >
            <div 
              className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border-2 border-indigo-500/50 overflow-hidden"
              style={{ boxShadow: '0 0 60px rgba(99, 102, 241, 0.3)' }}
            >
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-500/10" />
              
              {/* Currency icon header */}
              <div className={`relative bg-gradient-to-r ${currencyType?.color || 'from-indigo-500 to-purple-600'} p-4`}>
                <div className="flex items-center justify-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <Hash className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-white">{mainData.name || 'Currency'}</h3>
                    <p className="text-xs text-white/70">{currencyType?.title}</p>
                  </div>
                </div>
              </div>

              {/* Currency details */}
              <div className="relative p-4 space-y-2">
                <ValueRow 
                  label="Type" 
                  value={currencyType?.id === 'token' ? 'Token' : 
                         currencyType?.id === 'basket' ? 'Basket Currency' :
                         currencyType?.id === 'pbaas' ? 'PBaaS Chain' :
                         currencyType?.id === 'erc20' ? 'ERC-20 Mapped' : 'Currency'} 
                  highlight 
                />
                <ValueRow 
                  label="Proof Protocol" 
                  value={mainData.proofprotocol === 2 ? 'Mintable' : 
                         mainData.proofprotocol === 3 ? 'ERC-20' : 'Standard'} 
                />
                {mainData.initialsupply > 0 && (
                  <ValueRow label="Initial Supply" value={Number(mainData.initialsupply).toLocaleString()} />
                )}
                {currencyType?.id === 'basket' && (
                  <ValueRow label="Basket Type" value="Fractional Reserve" highlight />
                )}
                {mainData.isMintable && (
                  <div className="flex items-center gap-2 bg-emerald-500/10 rounded-lg p-2 border border-emerald-500/30 mt-2">
                    <Zap className="w-4 h-4 text-emerald-400" />
                    <span className="text-xs text-emerald-400">Mintable Currency</span>
                  </div>
                )}
              </div>
            </div>

            {/* Decorative arrows */}
            {(reserves.length > 0 || preallocations.length > 0) && (
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="absolute left-0 top-1/2 -translate-x-full -translate-y-1/2 flex items-center"
              >
                <div className="w-8 h-0.5 bg-gradient-to-r from-transparent to-indigo-500" />
                <ArrowRight className="w-4 h-4 text-indigo-500 -ml-1" />
              </motion.div>
            )}

            {(advancedNode || nodeSettingsNode || bridgeNode) && (
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="absolute right-0 top-1/2 translate-x-full -translate-y-1/2 flex items-center"
              >
                <ArrowRight className="w-4 h-4 text-indigo-500 -mr-1" />
                <div className="w-8 h-0.5 bg-gradient-to-r from-indigo-500 to-transparent" />
              </motion.div>
            )}
          </motion.div>

          {/* Legend */}
          <motion.div 
            variants={itemVariants}
            className="mt-6 bg-slate-800 rounded-lg p-3 border border-slate-600"
          >
            <div className="flex flex-wrap gap-3 justify-center">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-gradient-to-br from-amber-500 to-orange-600" />
                <span className="text-[10px] text-slate-300">Reserves</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-gradient-to-br from-emerald-500 to-green-600" />
                <span className="text-[10px] text-slate-300">Preallocations</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-gradient-to-br from-violet-500 to-purple-600" />
                <span className="text-[10px] text-slate-300">Eras</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-gradient-to-br from-pink-500 to-rose-600" />
                <span className="text-[10px] text-slate-300">Settings</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Column - Settings & Output */}
        <div className="col-span-4 space-y-4">
          {/* Bootstrap Nodes */}
          {nodeSettingsNode?.data?.networkaddress && (
            <Block 
              icon={Server} 
              title="Bootstrap Nodes" 
              color="from-pink-500 to-rose-600"
              glowColor="#ec4899"
            >
              <div className="bg-slate-700 rounded-lg p-2 border border-slate-500">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-pink-400" />
                  <span className="text-xs text-white font-mono truncate">
                    {nodeSettingsNode.data.networkaddress}
                  </span>
                </div>
                {nodeSettingsNode.data.nodeidentity && (
                  <div className="mt-1 text-[10px] text-slate-300">
                    Identity: {nodeSettingsNode.data.nodeidentity}
                  </div>
                )}
              </div>
            </Block>
          )}

          {/* Advanced Options */}
          {advancedNode && (
            <Block 
              icon={Settings} 
              title="Advanced Options" 
              color="from-cyan-500 to-blue-600"
              glowColor="#06b6d4"
            >
              <div className="space-y-1">
                {advancedNode.data.idregistrationfees && (
                  <ValueRow label="ID Registration" value={`${advancedNode.data.idregistrationfees} coins`} />
                )}
                {advancedNode.data.idreferrallevels && (
                  <ValueRow label="Referral Levels" value={advancedNode.data.idreferrallevels} />
                )}
                {advancedNode.data.blocktime && (
                  <ValueRow label="Block Time" value={`${advancedNode.data.blocktime}s`} />
                )}
                {advancedNode.data.notarizationreward && (
                  <ValueRow label="Notarization Reward" value={advancedNode.data.notarizationreward} />
                )}
                {advancedNode.data.powaveragingwindow && (
                  <ValueRow label="PoW Window" value={`${advancedNode.data.powaveragingwindow} blocks`} />
                )}
              </div>
            </Block>
          )}

          {/* Bridge Converter */}
          {bridgeNode && (
            <Block 
              icon={Link2} 
              title="Bridge Converter" 
              color="from-emerald-500 to-teal-600"
              glowColor="#10b981"
            >
              <div className="space-y-2">
                <ValueRow label="Name" value={bridgeNode.data.name} highlight />
                {bridgeNode.data.initialsupply && (
                  <ValueRow label="Initial Supply" value={Number(bridgeNode.data.initialsupply).toLocaleString()} />
                )}
                {bridgeNode.data.currencies && (
                  <div className="mt-2">
                    <div className="text-[10px] text-slate-300 mb-1">Bridge Currencies</div>
                    <div className="flex flex-wrap gap-1">
                      {bridgeNode.data.currencies.map((curr, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-emerald-600 text-emerald-100 text-[10px] rounded-full">
                          {curr}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Block>
          )}

          {/* Gateway Converter */}
          {gatewayNode && (
            <Block 
              icon={Zap} 
              title="Gateway Converter" 
              color="from-yellow-500 to-amber-600"
              glowColor="#eab308"
            >
              <div className="space-y-2">
                <ValueRow label="Name" value={gatewayNode.data.name} highlight />
                {gatewayNode.data.initialsupply && (
                  <ValueRow label="Initial Supply" value={Number(gatewayNode.data.initialsupply).toLocaleString()} />
                )}
                {gatewayNode.data.currencies && (
                  <div className="mt-2">
                    <div className="text-[10px] text-slate-300 mb-1">Currencies</div>
                    <div className="space-y-1">
                      {gatewayNode.data.currencies.map((curr, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-slate-700 rounded p-1.5 border border-slate-500">
                          <span className="text-[10px] text-white">{curr.currency}</span>
                          <span className="text-[10px] text-amber-400">{(curr.weight * 100).toFixed(0)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Block>
          )}

          {/* Summary Stats */}
          <Block 
            icon={Percent} 
            title="Summary" 
            color="from-slate-500 to-slate-600"
          >
            <div className="space-y-1">
              <ValueRow label="Reserve Currencies" value={reserves.length} />
              <ValueRow label="Preallocations" value={preallocations.length} />
              <ValueRow label="Mining Eras" value={eras.length > 0 && eras[0]?.reward ? eras.length : 0} />
              <ValueRow 
                label="Configuration" 
                value={currencyType?.id === 'pbaas' ? 'Full Chain' : 'Currency Only'} 
                highlight 
              />
            </div>
          </Block>
        </div>
      </div>
    </motion.div>
  );
};

export default CurrencyStructureDiagram;
